import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  Center,
  FormControl,
  FormLabel,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Select,
  ModalFooter,
  VStack,
  Text,
  HStack,
  Tooltip,
  IconButton,
  useToast,
  Skeleton,
  SkeletonText,
  Box,
  Grid,
  GridItem,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  addJobEducationCourseAsync,
  deleteJobEducationCourseAsync,
  fetchEducationCourseAsync,
  selectJobEducationCourse,
  updateJobEducationCourseAsync,
} from "../../../JobPostingSlice/JobEducationCourse/JobEducationCourceSlice";
import { useForm } from "react-hook-form";
import {
  fetchJobEducationAsync,
  selectJobEducation,
} from "../../../JobPostingSlice/JobEducation/JobEducationSlice";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";

function JobEducationCourse() {
  const dispatch = useDispatch();
  const jobEducationCourse = useSelector(selectJobEducationCourse);
  const toast = useToast();

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchEducationCourseAsync());
      } catch (error) {
        toast({
          title: "Error loading data",
          description: "Failed to load education courses",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, toast]);

  const {
    isOpen: isOpen1,
    onOpen: onOpen1,
    onClose: onClose1,
  } = useDisclosure();
  const {
    isOpen: isOpen2,
    onOpen: onOpen2,
    onClose: onClose2,
  } = useDisclosure();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const [newJobEducationCourse, setNewJobEducationCourse] = useState("");
  const [newJobStatus, setNewJobStatus] = useState(""); // Add status state
  const [error, setError] = useState(null);
  const [editJobEducationCourse, setEditJobEducationCourse] = useState("");
  const [editJobEducation, setEditJobEducation] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editJobStatus, setEditJobStatus] = useState("");
  const [editableJobEducationCourse, setEditableJobEducationCourse] =
    useState(null);
  const [deletingEducationCourse, setDeletingEducationCourse] = useState(null);

  const jobEducation = useSelector(selectJobEducation);

  useEffect(() => {
    dispatch(fetchJobEducationAsync());
    dispatch(fetchEducationCourseAsync());
  }, [dispatch]);

  const handleDelete = async (uuid) => {
    setIsDeleting(true);
    try {
      await dispatch(deleteJobEducationCourseAsync(uuid));
      await dispatch(fetchEducationCourseAsync());
      toast({
        title: "Success",
        description: "Education course deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      onClose2();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete education course",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (job) => {
    setDeletingEducationCourse(job);
    onOpen2();
  };

  const handleSave = async (newJobEducationCourse) => {
    setIsSubmitting(true);
    try {
      await dispatch(addJobEducationCourseAsync(newJobEducationCourse));
      await dispatch(fetchEducationCourseAsync());

      toast({
        title: "Success",
        description: "Education course added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      onClose1();
      reset();
    } catch (error) {
      console.error("Failed to add project", error);
      toast({
        title: "Error",
        description: "Failed to add education course",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (jobTitle) => {
    setEditableJobEducationCourse(jobTitle);
    setEditJobEducationCourse(jobTitle.title);
    setEditJobStatus(jobTitle.status);
    setEditJobEducation(jobTitle.core_job_education.uuid);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editJobEducationCourse.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a course title",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsUpdating(true);
    try {
      await dispatch(
        updateJobEducationCourseAsync({
          uuid: editableJobEducationCourse.uuid,
          jobEducationCourseData: {
            title: editJobEducationCourse,
            status: editJobStatus,
            job_education_uuid: editJobEducation,
          },
        })
      );

      await dispatch(fetchEducationCourseAsync());

      toast({
        title: "Success",
        description: "Education course updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setEditableJobEducationCourse(null);
      setEditJobEducationCourse("");
      setEditJobStatus("");
      setEditJobEducation("");
      setIsEditModalOpen(false);
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to update education course",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const SkeletonRows = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <Tr key={index}>
          <Td>
            <Skeleton height="20px" width="120px" />
          </Td>
          <Td>
            <Skeleton height="20px" width="150px" />
          </Td>
          <Td>
            <Skeleton height="20px" width="60px" />
          </Td>
          <Td>
            <HStack spacing={2}>
              <Skeleton height="32px" width="32px" borderRadius="md" />
              <Skeleton height="32px" width="32px" borderRadius="md" />
            </HStack>
          </Td>
        </Tr>
      ))}
    </>
  );

  return (
    <div
      className="min-h-screen bg-adminbg p-4 contentAdmin"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="">
        <div className="w-full my-5 bg-white rounded-md">
          <div className="flex justify-between">
            <Box className="px-3 py-7">
              {isLoading ? (
                <SkeletonText noOfLines={1} skeletonHeight="28px" width="200px" />
              ) : (
                <p className="text-xl text-black font-semibold">
                  Education Courses
                </p>
              )}
            </Box>
            <div className="py-4 px-3 flex gap-3">
              <Button
                colorScheme="blue"
                fontSize={"medium"}
                onClick={onOpen1}
                isDisabled={isLoading}
              >
                Add<i className="fa-solid fa-plus px-2"></i>
              </Button>

              {/* Add Job Education Course Modal */}
              <Modal isOpen={isOpen1} onClose={onClose1} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Add Job Education Course
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <form onSubmit={handleSubmit(handleSave)}>
                    <ModalBody py={6}>
                      <VStack spacing={6}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                          <GridItem colSpan={2}>
                            <FormControl isInvalid={errors.title}>
                              <FormLabel>Job Education Course</FormLabel>
                              <Input
                                color="black"
                                placeholder="Enter Job Education Course"
                                {...register("title", { required: "This field is required" })}
                                isDisabled={isSubmitting}
                              />
                              <FormErrorMessage>
                                {errors.title && errors.title.message}
                              </FormErrorMessage>
                            </FormControl>
                          </GridItem>

                          <GridItem colSpan={1}>
                            <FormControl isInvalid={errors.status}>
                              <FormLabel>Course Status</FormLabel>
                              <Select
                                placeholder="Select Course Status"
                                color="black"
                                borderColor="black"
                                {...register("status", { required: "This field is required" })}
                                isDisabled={isSubmitting}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Select>
                              <FormErrorMessage>
                                {errors.status && errors.status.message}
                              </FormErrorMessage>
                            </FormControl>
                          </GridItem>

                          <GridItem colSpan={1}>
                            <FormControl isInvalid={errors.job_education_uuid}>
                              <FormLabel>Job Education</FormLabel>
                              <Select
                                color="black"
                                placeholder="Select Job Education"
                                {...register("job_education_uuid", { required: "This field is required" })}
                                isDisabled={isSubmitting}
                              >
                                {Array.isArray(jobEducation) &&
                                  jobEducation.map((education) => (
                                    <option key={education.uuid} value={education.uuid}>
                                      {education.title}
                                    </option>
                                  ))}
                              </Select>
                              <FormErrorMessage>
                                {errors.job_education_uuid && errors.job_education_uuid.message}
                              </FormErrorMessage>
                            </FormControl>
                          </GridItem>
                        </Grid>
                      </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                      <Button
                        colorScheme="blue"
                        mr={3}
                        type="submit"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                      >
                        Save
                      </Button>
                      <Button
                        onClick={onClose1}
                        isDisabled={isSubmitting}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                    </ModalFooter>
                  </form>
                </ModalContent>
              </Modal>

              {/* Edit Job Education Course Modal */}
              <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="4xl">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                    Edit Job Education Course
                  </ModalHeader>
                  <ModalCloseButton color="white" />
                  <ModalBody py={6}>
                    <VStack spacing={6}>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Job Education Course</FormLabel>
                            <Input
                              color="black"
                              placeholder="Enter Job Education Course title"
                              value={editJobEducationCourse}
                              onChange={(e) => setEditJobEducationCourse(e.target.value)}
                              isDisabled={isUpdating}
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem colSpan={1}>
                          <FormControl>
                            <FormLabel>Course Status</FormLabel>
                            <Select
                              placeholder="Select Course Status"
                              value={editJobStatus}
                              onChange={(e) => setEditJobStatus(e.target.value)}
                              color="black"
                              borderColor="black"
                              isDisabled={isUpdating}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </Select>
                          </FormControl>
                        </GridItem>

                        <GridItem colSpan={1}>
                          <FormControl>
                            <FormLabel>Job Education</FormLabel>
                            <Select
                              color="black"
                              placeholder="Select Job Education"
                              value={editJobEducation}
                              onChange={(e) => setEditJobEducation(e.target.value)}
                              isDisabled={isUpdating}
                            >
                              {Array.isArray(jobEducation) &&
                                jobEducation.map((education) => (
                                  <option key={education?.uuid} value={education?.uuid}>
                                    {education?.title}
                                  </option>
                                ))}
                            </Select>
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </VStack>
                    {error && (
                      <Text color="red.500" mt={4} textAlign="center">
                        {error}
                      </Text>
                    )}
                  </ModalBody>
                  <ModalFooter bg="gray.50" borderBottomRadius="md">
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={handleUpdate}
                      isLoading={isUpdating}
                      loadingText="Updating..."
                    >
                      Update
                    </Button>
                    <Button
                      onClick={() => setIsEditModalOpen(false)}
                      isDisabled={isUpdating}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* Delete Job Education Course Modal */}
              <Modal isOpen={isOpen2} onClose={onClose2}>
                <ModalOverlay />
                <ModalContent maxW="2xl" borderRadius="lg" boxShadow="lg">
                  <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                    Confirm Deletion
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text fontSize="md" textAlign="center">
                      Are you sure you want to delete{" "}
                      <Text as="span" fontWeight="semibold" color="red.500">
                        "{deletingEducationCourse?.title}"
                      </Text>
                      ?
                    </Text>
                  </ModalBody>
                  <ModalFooter display="flex" gap="2" justifyContent="center">
                    <Button
                      colorScheme="red"
                      w="40"
                      onClick={() => handleDelete(deletingEducationCourse.uuid)}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="red"
                      w="40"
                      onClick={onClose2}
                      isDisabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </div>
          </div>

          <TableContainer>
            <Table size="sm">
              <Thead className="bg-gray-200">
                <Tr>
                  <Th className="w-1/4">Degree</Th>
                  <Th className="w-1/4">Title</Th>
                  <Th className="w-1/4">Status</Th>
                  <Th className="w-1/4">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <SkeletonRows />
                ) : Array.isArray(jobEducationCourse) && jobEducationCourse.length > 0 ? (
                  jobEducationCourse.map((job) => (
                    <Tr key={job.id}>
                      <Td>{job?.core_job_education?.title}</Td>
                      <Td>{job.title}</Td>
                      <Td textColor={"green"}>{job.status}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Edit Courses" placement="top">
                            <IconButton
                              icon={<EditIcon />}
                              colorScheme="blue"
                              size="sm"
                              onClick={() => handleEdit(job)}
                              aria-label="Edit course"
                            />
                          </Tooltip>
                          <Tooltip label="Delete Courses" placement="top">
                            <IconButton
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              size="sm"
                              onClick={() => openDeleteModal(job)}
                              aria-label="Delete course"
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="4" className="text-center py-4 text-gray-500">
                      No data available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default JobEducationCourse;